import { useContext } from "react";
import { SegmentGroup, Segment, Text } from "@simplybusiness/mobius";
import { CombinedPaymentContext } from "../../../shared/PersistentSummary/CombinedPaymentContext";
import { PaymentItemsSection } from "./PaymentItemsSection";
import { TotalRow } from "./TotalRow";
import type { LabelValue, PaymentItem } from "./types";

export type PaymentSummaryProps = {
  /** Primary trade description */
  primaryTrade: string;
  /** Business name to display */
  businessName: string;
  /** Upfront payment breakdown */
  upfrontPayments: {
    items: PaymentItem[];
    total: string;
  };
  /** Monthly payment breakdown */
  monthlyPayments: {
    items: PaymentItem[];
    summary: LabelValue[];
    firstPaymentDate: string;
    hasFees: boolean;
    isExternalCarrier: boolean;
  };
  /** Total amount for all payments */
  totalPaymentAmount: string;
  /** Legal text notices */
  legalTexts: {
    multiProductText: string;
    nonRefundableTexts: string[];
    surchargeTexts: string[];
    cardFeeDisclosureText: string;
  };
};

export const PaymentSummary = ({
  primaryTrade,
  businessName,
  upfrontPayments,
  monthlyPayments,
  totalPaymentAmount,
  legalTexts,
}: PaymentSummaryProps) => {
  const isCombinedPaymentPage = useContext(CombinedPaymentContext);
  const hasMonthlyPayments = monthlyPayments.items.length > 0;

  return (
    <div className="mobius mobius-payment-summary">
      <SegmentGroup data-testid="payment-summary">
        {!isCombinedPaymentPage && (
          <Segment data-testid="business-information-section">
            <div className="mobius-payment-summary__item-grid">
              <Text variant="h4">{`${primaryTrade} insurance`}</Text>
              <Text variant="small" spacing="tight">
                <strong>{businessName}</strong>
              </Text>
            </div>
          </Segment>
        )}
        <PaymentItemsSection
          headerText="Today's payment"
          items={upfrontPayments.items}
          summaryItems={
            hasMonthlyPayments
              ? [{ label: "To pay today", value: upfrontPayments.total }]
              : undefined
          }
          isCombined={isCombinedPaymentPage}
        />
        {hasMonthlyPayments && (
          <PaymentItemsSection
            headerText="Monthly payments"
            headerItem={{
              label: "First monthly payment taken",
              value: monthlyPayments.firstPaymentDate,
            }}
            items={monthlyPayments.items}
            summaryItems={
              monthlyPayments.hasFees ? monthlyPayments.summary : undefined
            }
            isExternalCarrier={monthlyPayments.isExternalCarrier}
            isCombined={isCombinedPaymentPage}
          />
        )}
        <Segment colour="subtle">
          <TotalRow
            amount={totalPaymentAmount}
            isBold={isCombinedPaymentPage}
          />
        </Segment>
      </SegmentGroup>
      <Text variant="legal">{legalTexts.multiProductText}</Text>
      {legalTexts.nonRefundableTexts.map((text, index) => (
        <Text variant="legal" key={`non-refundable-${index}`}>
          {text}
        </Text>
      ))}
      {legalTexts.surchargeTexts.map((text, index) => (
        <Text variant="legal" key={`surcharge-${index}`}>
          {text}
        </Text>
      ))}
      <Text variant="legal">{legalTexts.cardFeeDisclosureText}</Text>
    </div>
  );
};

PaymentSummary.displayName = "PaymentSummary";
